import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IDatatableOutputDTO,
  IDatatableParams,
} from 'src/app/shared/datatables/datatables';
import { HttpParamsMapper } from 'src/app/shared/mappers/http-params.mapper';
import { environment } from 'src/environments/environment';

export interface ConsolidatedScheduleHistory {
  id: string;
  startDate: string;
  endDate: string;
  consolidatedAt: string | null;
}

export interface ConsolidatedScheduleRow {
  consolidatedScheduleId: string;
  indicationId: number;
  appointmentDate: Date | string | number | null;
  hasDocument: boolean;
  doctorName: string | null;
  patientName: string | null;
  specialtyName: string | null;
  amount: number | null;
  complementaryServices?: string | null;
  serviceDoctorAmount?: number | null;
  totalDoctorToPay?: number | null;
  transferStatus?: string | null;
  paymentFailReason?: string | null;
  transferId?: string | null;
  transferReceiptUrl?: string | null;
  nfseIssued?: boolean | null;
  nfseStatus?: string | null;
  nfseUrl?: string | null;
  manualPaymentReason?: string | null;
  method?: 'AUTOMATIC' | 'MANUAL' | null;
  paymentDate?: Date | string | number | null;
  planName: string;
}

export interface ConsolidatedSchedulesFilter {
  consolidatedHistoryId?: string;
  doctorName?: string | null;
}

export type PaidNFSeStatusFilter = 'emitted' | 'not_emitted' | 'error';

export interface PaidConsolidatedSchedulesFilter
  extends ConsolidatedSchedulesFilter {
  doctorName?: string | null;
  nfseStatus?: PaidNFSeStatusFilter | null;
}

export interface ConsolidatedSchedulesResponse
  extends IDatatableOutputDTO<ConsolidatedScheduleRow> {
  selectedHistory: ConsolidatedScheduleHistory | null;
  availableHistories: ConsolidatedScheduleHistory[];
}

@Injectable({ providedIn: 'root' })
export class AdministrativeConsolidatedSchedulesReportService {
  private readonly url = `${environment.api_v2}/v2/administrative/financial/consolidated-schedules`;

  constructor(private readonly http: HttpClient) {}

  searchAvailable({
    filter,
    page,
    perPage,
  }: IDatatableParams<ConsolidatedSchedulesFilter>): Observable<ConsolidatedSchedulesResponse> {
    const params = HttpParamsMapper.toHttpParams({
      filter: JSON.stringify(filter ?? {}),
      page,
      perPage,
    });
    return this.http.get<ConsolidatedSchedulesResponse>(
      `${this.url}/available`,
      { params }
    );
  }

  searchUnavailable({
    filter,
    page,
    perPage,
  }: IDatatableParams<ConsolidatedSchedulesFilter>): Observable<ConsolidatedSchedulesResponse> {
    const params = HttpParamsMapper.toHttpParams({
      filter: JSON.stringify(filter ?? {}),
      page,
      perPage,
    });

    const url = `${this.url}/unavailable`;
    return this.http.get<ConsolidatedSchedulesResponse>(url, { params });
  }

  searchPaid({
    filter,
    page,
    perPage,
  }: IDatatableParams<PaidConsolidatedSchedulesFilter>): Observable<ConsolidatedSchedulesResponse> {
    const params = HttpParamsMapper.toHttpParams({
      filter: JSON.stringify(filter ?? {}),
      page,
      perPage,
    });

    const url = `${this.url}/paid`;
    return this.http.get<ConsolidatedSchedulesResponse>(url, { params });
  }
}
